(() => {
const twoShirtV4Analytics = window.__lexiboTwoShirtV4Analytics || (() => {
  const measurementId = 'G-SP7RV0ZSQ6';
  const localEventName = 'lexibo:two-shirt-v4';
  const debugStorageKey = 'lexibo:two-shirt-v4:ga4-debug';
  const previewThemeRoles = new Set(['development', 'unpublished', 'demo']);
  const eventNames = new Set([
    'two_shirt_v4_view',
    'two_shirt_v4_motif_changed',
    'two_shirt_v4_motifs_complete',
    'two_shirt_v4_step_view',
    'two_shirt_v4_size_mode_changed',
    'two_shirt_v4_size_selected',
    'two_shirt_v4_selection_complete',
    'two_shirt_v4_add_clicked',
    'two_shirt_v4_add_succeeded',
    'two_shirt_v4_add_failed',
  ]);
  const pendingEvents = [];
  const consentListeners = new Set();
  const maxPendingEvents = 50;
  let consentState = 'unknown';

  const clonePayload = (payload) => ({
    ...payload,
    ...(Array.isArray(payload.items) ? { items: payload.items.map((item) => ({ ...item })) } : {}),
  });

  const themeRole = () => window.Shopify?.theme?.role || 'unknown';
  const isPreviewTheme = () => previewThemeRoles.has(themeRole())
    || new URLSearchParams(window.location.search).has('preview_theme_id');

  const previewDebugEnabled = () => {
    const debugParameter = new URLSearchParams(window.location.search).get('v4_tracking_debug');
    try {
      if (debugParameter === '1') window.sessionStorage.setItem(debugStorageKey, '1');
      if (debugParameter === '0') window.sessionStorage.removeItem(debugStorageKey);
      return debugParameter === '1' || window.sessionStorage.getItem(debugStorageKey) === '1';
    } catch (_error) {
      return debugParameter === '1';
    }
  };

  const directGaEnabled = () => !isPreviewTheme() || previewDebugEnabled();

  const ensureDataLayer = () => {
    if (!window.dataLayer || typeof window.dataLayer.push !== 'function') window.dataLayer = [];
    return window.dataLayer;
  };

  const hasGoogleTagScript = () => [...document.scripts].some((script) => {
    try {
      const url = new URL(script.src, window.location.href);
      return url.hostname === 'www.googletagmanager.com'
        && url.pathname === '/gtag/js'
        && url.searchParams.get('id') === measurementId;
    } catch (_error) {
      return false;
    }
  });

  const initializeGoogleTag = () => {
    ensureDataLayer();
    window.gtag = window.gtag || function () {
      ensureDataLayer().push(arguments);
    };
    window.__lexiboConfiguredGa4Ids = window.__lexiboConfiguredGa4Ids || {};

    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    if (!window.__lexiboConfiguredGa4Ids[measurementId]) {
      window.__lexiboConfiguredGa4Ids[measurementId] = true;
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        send_page_view: false,
        allow_google_signals: false,
        allow_ad_personalization_signals: false,
      });
    }

    if (!hasGoogleTagScript()) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
      script.dataset.lexiboTwoShirtV4Ga4 = '';
      document.head.appendChild(script);
    }
  };

  const ga4Parameters = (payload) => {
    const { event: _event, ...parameters } = payload;
    Object.entries(parameters).forEach(([key, value]) => {
      if (Array.isArray(value) && key !== 'items') parameters[key] = value.join('|');
    });
    return {
      ...parameters,
      send_to: measurementId,
      ...(isPreviewTheme() ? { debug_mode: true } : {}),
    };
  };

  const sendToDestinations = (payload) => {
    if (!directGaEnabled()) return;
    try {
      initializeGoogleTag();
      window.gtag('event', payload.event, ga4Parameters(payload));
    } catch (_error) {
      // Analytics failures must never affect the purchase flow.
    }
  };

  const notifyConsentListeners = () => {
    consentListeners.forEach((listener) => {
      try {
        listener(consentState);
      } catch (_error) {
        // One listener must not prevent the remaining consent updates.
      }
    });
  };

  const setConsentState = (nextState) => {
    if (!['granted', 'denied'].includes(nextState) || nextState === consentState) return;
    const previousState = consentState;
    consentState = nextState;

    if (nextState === 'denied') {
      pendingEvents.splice(0);
      if (window.__lexiboConfiguredGa4Ids?.[measurementId] && window.gtag) {
        try {
          window.gtag('consent', 'update', {
            analytics_storage: 'denied',
            ad_storage: 'denied',
            ad_user_data: 'denied',
            ad_personalization: 'denied',
          });
        } catch (_error) {
          // Consent state remains authoritative even when a third-party gtag is broken.
        }
      }
      notifyConsentListeners();
      return;
    }

    notifyConsentListeners();
    if (previousState === 'unknown') pendingEvents.splice(0).forEach(sendToDestinations);
  };

  const updateAnalyticsConsent = () => {
    const customerPrivacy = window.Shopify?.customerPrivacy;
    if (!customerPrivacy?.analyticsProcessingAllowed) return;
    try {
      if (customerPrivacy.analyticsProcessingAllowed()) {
        setConsentState('granted');
        return;
      }
      const trackingConsent = customerPrivacy.getTrackingConsent?.();
      const visitorConsent = customerPrivacy.currentVisitorConsent?.();
      const analyticsConsent = visitorConsent?.analytics;
      if (trackingConsent === 'no_interaction' || analyticsConsent === '') return;
      setConsentState('denied');
    } catch (_error) {
      setConsentState('denied');
    }
  };

  const initializePrivacy = () => {
    if (window.Shopify?.customerPrivacy) {
      updateAnalyticsConsent();
      return;
    }
    if (!window.Shopify?.loadFeatures) return;
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      if (!error) updateAnalyticsConsent();
    });
  };

  const track = (eventName, detail = {}) => {
    if (!eventNames.has(eventName)) return null;
    const payload = {
      event: eventName,
      experience_id: 'two_shirt_v4',
      picker_version: 'v4',
      picker_environment: isPreviewTheme() ? 'preview' : 'live',
      theme_role: themeRole(),
      ...detail,
    };
    const localPayload = clonePayload(payload);
    try {
      document.dispatchEvent(new CustomEvent(localEventName, { detail: localPayload }));
    } catch (_error) {
      // The local debug bus is optional and must not affect the picker.
    }

    if (consentState === 'granted') {
      try {
        sendToDestinations(payload);
      } catch (_error) {
        // Keep analytics strictly non-blocking.
      }
    } else if (consentState === 'unknown') {
      if (pendingEvents.length >= maxPendingEvents) pendingEvents.shift();
      pendingEvents.push(clonePayload(payload));
    }
    return localPayload;
  };

  const onConsentChange = (listener) => {
    if (typeof listener !== 'function') return () => {};
    consentListeners.add(listener);
    listener(consentState);
    return () => consentListeners.delete(listener);
  };

  document.addEventListener('visitorConsentCollected', updateAnalyticsConsent);
  initializePrivacy();

  return {
    track,
    onConsentChange,
    isAnalyticsAllowed: () => consentState === 'granted',
    debugSnapshot: () => ({
      consentState,
      pendingEventCount: pendingEvents.length,
      directGaEnabled: directGaEnabled(),
      previewDebugEnabled: previewDebugEnabled(),
    }),
  };
})();

window.__lexiboTwoShirtV4Analytics = twoShirtV4Analytics;

if (!customElements.get('free-two-shirt-v4-builder')) {
  customElements.define(
    'free-two-shirt-v4-builder',
    class FreeTwoShirtBuilderV4 extends HTMLElement {
      constructor() {
        super();
        this.selectedKeys = [];
        this.mode = 'shared';
        this.sharedSize = null;
        this.separateSizes = new Map();
        this.liveUnavailable = new Set();
        this.isSubmitting = false;
        this.addSucceeded = false;
        this.submissionError = false;
        this.currentStep = 'motifs';
        this.suppressCardClick = false;
        this.activeCardIndex = 0;
        this.successOpenTimer = null;
        this.storageVersion = 4;
        this.storageTtl = 24 * 60 * 60 * 1000;
        this.analytics = twoShirtV4Analytics;
        this.attribution = {};
        this.attributionStorageKey = 'lexibo:two-shirt-v4:attribution:v1';
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.reducedMotion = this.reducedMotionQuery.matches;
      }

      connectedCallback() {
        if (this.initialized) {
          this.bindGlobalEvents();
          this.bindAnalyticsConsent();
          return;
        }
        this.initialized = true;
        this.captureAttribution();

        this.deck = this.querySelector('[data-product-deck]');
        this.cards = [...this.querySelectorAll('[data-shirt-card]')];
        this.slots = [...this.querySelectorAll('[data-selection-slot]')];
        this.selectionCount = this.querySelector('[data-selection-count]');
        this.stepKicker = this.querySelector('.free-two-shirt-v4__motif-step .free-two-shirt-v4__step-kicker');
        this.continueButton = this.querySelector('[data-go-sizes]');
        this.sizeStep = this.querySelector('[data-size-step]');
        this.motifStep = this.querySelector('[data-motif-step]');
        this.stageAnchor = this.querySelector('[data-stage-anchor]');
        this.sharedPanel = this.querySelector('[data-shared-sizes]');
        this.separatePanel = this.querySelector('[data-separate-sizes]');
        this.separateGroups = this.querySelector('[data-separate-size-groups]');
        this.chosenProducts = this.querySelector('[data-chosen-products]');
        this.addButton = this.querySelector('[data-add-both]');
        this.addLabel = this.querySelector('[data-add-label]');
        this.message = this.querySelector('[data-builder-message]');
        this.liveStatus = this.querySelector('[data-selection-status]');
        this.successDialog = this.querySelector('[data-success-dialog]');
        this.dialogOverlay = this.querySelector('[data-dialog-overlay]');
        this.previousButton = this.querySelector('[data-deck-previous]');
        this.nextButton = this.querySelector('[data-deck-next]');
        this.dots = this.querySelector('[data-deck-dots]');
        this.appBar = this.querySelector('[data-app-bar]');
        this.appMotifStep = this.querySelector('[data-app-step="motifs"]');
        this.appSizeStep = this.querySelector('[data-app-step="sizes"]');
        this.appCta = this.querySelector('[data-app-cta]');
        this.appCtaLabel = this.querySelector('[data-app-cta-label]');
        this.appMotifCount = this.querySelector('[data-app-motif-count]');
        this.appSizeState = this.querySelector('[data-app-size-state]');
        this.duoPrice = this.querySelector('[data-duo-price]');
        this.initialDuoPrice = this.duoPrice?.textContent || '';
        this.lastSuccess = null;

        try {
          this.products = [...this.querySelectorAll('[data-shirt-product]')]
            .map((payload) => JSON.parse(payload.textContent))
            .map((product) => this.normalizeProduct(product));
        } catch (error) {
          console.error('Die freie Zwei-Shirt-Auswahl konnte nicht initialisiert werden.', error);
          this.showConfigurationMessage('Die Produktdaten konnten nicht geladen werden. Bitte prüfe die Section-Konfiguration.');
          this.bindAnalyticsConsent();
          this.analytics.track('two_shirt_v4_view', {
            configuration_state: 'payload_error',
            entry_state: 'unavailable',
            entry_step: 'motifs',
            restore_status: 'unavailable',
            selection_count: 0,
            size_mode: 'shared',
          });
          return;
        }

        this.productsByKey = new Map(this.products.map((product) => [product.key, product]));
        this.initializePersistence();
        const distinctProductIds = new Set(this.products.map((product) => String(product.id)));
        this.isConfigured = Number(this.dataset.configuredCount) === 6
          && this.products.length === 6
          && distinctProductIds.size === 6
          && this.products.every((product) => product.valid);

        this.hydrateCards();
        const restoreStatus = this.restorePersistentState();
        this.createDots();
        this.bindEvents();
        this.updateDeckPosition();
        this.updateSelectionUI();
        this.bindAnalyticsConsent();

        if (!this.isConfigured) {
          const invalidProducts = this.products.filter((product) => !product.valid);
          const detail = invalidProducts.length
            ? ` Prüfe: ${invalidProducts.map((product) => product.displayTitle).join(', ')}.`
            : '';
          const uniquenessHint = distinctProductIds.size !== this.products.length
            ? ' Jedes Motiv muss ein anderes Produkt sein.'
            : '';
          this.showConfigurationMessage(`Für den Start müssen sechs gültige Shirt-Karten konfiguriert sein.${uniquenessHint}${detail}`);
        }
        this.trackView(restoreStatus);
      }

      disconnectedCallback() {
        this.clearSuccessOpenTimer();
        this.unbindGlobalEvents();
        this.unbindAnalyticsConsent();
        window.cancelAnimationFrame(this.deckFrame);
      }

      normalizeProduct(product) {
        const options = Array.isArray(product.options) ? product.options : [];
        const variants = (Array.isArray(product.variants) ? product.variants : []).map((variant) => ({
          ...variant,
          id: String(variant.id),
          options: Array.isArray(variant.options) ? variant.options.map(String) : [],
          price: Number(variant.price || 0),
          available: Boolean(variant.available),
        }));
        const colorIndex = options.findIndex((name) => /farbe|color|colour/i.test(name));
        let sizeIndex = options.findIndex((name) => /gr[oö]ße|size/i.test(name));

        if (sizeIndex < 0) {
          const nonColorIndexes = options.map((_name, index) => index).filter((index) => index !== colorIndex);
          if (nonColorIndexes.length === 1) sizeIndex = nonColorIndexes[0];
        }

        const requestedColor = String(product.fixedColor || '').trim();
        const availableColors = colorIndex >= 0
          ? [...new Set(variants.map((variant) => variant.options[colorIndex]).filter(Boolean))]
          : [];
        const matchedColor = requestedColor && colorIndex >= 0
          ? availableColors.find((color) => this.normalizeValue(color) === this.normalizeValue(requestedColor))
          : null;
        const fixedColor = colorIndex >= 0 ? (matchedColor || (!requestedColor ? availableColors[0] : '')) : requestedColor;
        const fixedVariants = variants.filter((variant) => {
          if (colorIndex < 0) return true;
          return this.normalizeValue(variant.options[colorIndex]) === this.normalizeValue(fixedColor);
        });
        const sizes = sizeIndex >= 0
          ? [...new Set(fixedVariants.map((variant) => variant.options[sizeIndex]).filter(Boolean))]
          : [];
        const valid = Boolean(fixedVariants.length && sizes.length && fixedVariants.some((variant) => variant.available));

        return {
          ...product,
          options,
          variants,
          colorIndex,
          sizeIndex,
          fixedColor,
          fixedVariants,
          sizes,
          valid,
          image: fixedVariants.find((variant) => variant.image)?.image || fixedVariants[0]?.image || null,
        };
      }

      normalizeValue(value) {
        return String(value || '').trim().toLocaleLowerCase('de-DE');
      }

      sanitizeAttributionValue(value) {
        return String(value || '')
          .replace(/[\u0000-\u001f\u007f]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          .slice(0, 200);
      }

      captureAttribution() {
        const params = new URLSearchParams(window.location.search);
        const attribution = {};
        ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
          const value = this.sanitizeAttributionValue(params.get(key));
          if (value) attribution[key] = value;
        });
        this.attribution = attribution;
      }

      bindAnalyticsConsent() {
        if (this.unbindAnalyticsConsentListener) return;
        this.unbindAnalyticsConsentListener = this.analytics.onConsentChange((state) => {
          this.handleAnalyticsConsent(state);
        });
      }

      unbindAnalyticsConsent() {
        this.unbindAnalyticsConsentListener?.();
        this.unbindAnalyticsConsentListener = null;
      }

      handleAnalyticsConsent(state) {
        if (state === 'denied') {
          this.attribution = {};
          try {
            window.sessionStorage.removeItem(this.attributionStorageKey);
          } catch (_error) {
            // Storage denial must not affect the picker.
          }
          if (this.dataset.cartUpdateUrl) {
            void this.clearPersistedAttribution().catch(() => {
              // Consent withdrawal remains authoritative even if cart cleanup is unavailable.
            });
          }
          return;
        }
        if (state !== 'granted') return;

        this.captureAttribution();
        const currentAttribution = { ...this.attribution };
        const hasCurrentAttribution = Object.keys(currentAttribution).length > 0;
        try {
          const stored = JSON.parse(window.sessionStorage.getItem(this.attributionStorageKey) || '{}');
          const storedValues = stored?.values && typeof stored.values === 'object' ? stored.values : {};
          const normalizedStoredValues = {};
          ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((key) => {
            const value = this.sanitizeAttributionValue(storedValues[key]);
            if (value) normalizedStoredValues[key] = value;
          });

          // Treat each URL's UTM set as one last-touch record. A new set replaces the
          // previous record instead of producing a hybrid of two different campaigns.
          this.attribution = hasCurrentAttribution ? currentAttribution : normalizedStoredValues;
          if (Object.keys(this.attribution).length) {
            window.sessionStorage.setItem(this.attributionStorageKey, JSON.stringify({
              version: 1,
              savedAt: Date.now(),
              values: this.attribution,
            }));
          } else {
            window.sessionStorage.removeItem(this.attributionStorageKey);
          }
        } catch (_error) {
          // The current URL remains the authoritative in-memory touch when storage fails.
          this.attribution = currentAttribution;
        }

        // A first-time visitor may finish the add before choosing analytics consent.
        // Backfill the cart marker if consent is granted while the successful picker is still open.
        if (this.addSucceeded) {
          void this.persistAttribution().catch(() => {
            // Attribution remains best-effort and must not affect the completed cart action.
          });
        }
      }

      analyticsStateDetail(state = this.getCheckoutState()) {
        return {
          selection_count: this.selectedKeys.length,
          size_mode: this.mode,
          step_name: this.currentStep,
          is_ready: Number(state.ready),
        };
      }

      getEntryState(state = this.getCheckoutState()) {
        if (state.ready) return 'ready';
        if (this.currentStep === 'sizes') return 'sizes_partial';
        if (this.selectedKeys.length === 2) return 'motifs_complete';
        if (this.selectedKeys.length === 1) return 'motifs_partial';
        return 'fresh';
      }

      trackView(restoreStatus) {
        const state = this.getCheckoutState();
        this.track('two_shirt_v4_view', {
          configuration_state: this.isConfigured ? 'ready' : 'invalid',
          entry_state: this.getEntryState(state),
          entry_step: this.currentStep,
          restore_status: restoreStatus,
        }, state);
      }

      track(eventName, detail = {}, state = this.getCheckoutState()) {
        return this.analytics.track(eventName, {
          ...this.analyticsStateDetail(state),
          ...detail,
        });
      }

      selectedSizeCount() {
        if (this.mode === 'shared') return this.sharedSize && this.isSharedSizeAvailable(this.sharedSize) ? 1 : 0;
        return this.getSelectedProducts().filter((product) => {
          return Boolean(this.getVariant(product, this.separateSizes.get(product.key)));
        }).length;
      }

      trackSelectionCompletion(wasReady, state = this.getCheckoutState()) {
        if (wasReady || !state.ready) return;
        this.track('two_shirt_v4_selection_complete', {
          currency: state.currency,
          value: Number((state.total / 100).toFixed(2)),
          sizes_selected: this.selectedSizeCount(),
          sizes_required: this.mode === 'shared' ? 1 : 2,
        }, state);
      }

      analyticsItems(selections) {
        return selections.map(({ product, size, variant }, index) => ({
          item_id: String(variant.id),
          item_name: product.displayTitle,
          item_variant: `${product.fixedColor || 'Ohne Farboption'} / ${size}`,
          item_list_id: 'two_shirt_v4',
          item_list_name: 'Zwei Shirts – freie Auswahl V4',
          index: index + 1,
          price: Number((Number(variant.price || 0) / 100).toFixed(2)),
          quantity: 1,
          product_id: String(product.id),
        }));
      }

      checkoutAnalyticsPayload(selections, state = this.getCheckoutState()) {
        return {
          currency: state.currency,
          value: Number((state.total / 100).toFixed(2)),
          items: this.analyticsItems(selections),
        };
      }

      initializePersistence() {
        const signatureSource = this.products.map((product) => {
          const variants = product.fixedVariants
            .map((variant) => variant.id)
            .join(',');
          return `${product.id}:${product.key}:${variants}`;
        }).join('|');
        this.productSignature = this.hashString(signatureSource);
        const shopScope = encodeURIComponent(this.dataset.shopScope || window.location.host);
        const sectionScope = encodeURIComponent(this.dataset.sectionId || 'section');
        this.storageScope = `${shopScope}:${sectionScope}`;
        this.storageKey = `lexibo:free-two-shirt-v4:v${this.storageVersion}:${this.storageScope}`;
      }

      hashString(value) {
        let hash = 2166136261;
        for (let index = 0; index < value.length; index += 1) {
          hash ^= value.charCodeAt(index);
          hash = Math.imul(hash, 16777619);
        }
        return (hash >>> 0).toString(36);
      }

      restorePersistentState() {
        if (!this.storageKey) return 'unavailable';
        let raw;
        try {
          raw = window.localStorage.getItem(this.storageKey);
        } catch (_error) {
          return 'unavailable';
        }

        if (raw === null) return 'missing';

        let stored;
        try {
          stored = JSON.parse(raw);
        } catch (_error) {
          this.clearPersistentState();
          return 'invalid';
        }

        if (!stored || typeof stored !== 'object' || Array.isArray(stored)) {
          this.clearPersistentState();
          return 'invalid';
        }
        const expired = !Number.isFinite(Number(stored.savedAt))
          || Date.now() - Number(stored.savedAt) > this.storageTtl;
        if (stored.version !== this.storageVersion
          || stored.scope !== this.storageScope
          || stored.productSignature !== this.productSignature
          || expired) {
          this.clearPersistentState();
          return 'invalid';
        }

        const keys = stored.selectedKeys;
        const validKeys = Array.isArray(keys)
          && keys.length <= 2
          && keys.every((key) => typeof key === 'string')
          && new Set(keys).size === keys.length
          && keys.every((key) => this.productsByKey.get(key)?.valid);
        if (!validKeys) {
          this.clearPersistentState();
          return 'invalid';
        }

        this.selectedKeys = [...keys];
        this.mode = ['shared', 'separate'].includes(stored.mode) ? stored.mode : 'shared';
        this.sharedSize = null;
        this.separateSizes.clear();

        if (this.selectedKeys.length === 2) {
          if (typeof stored.sharedSize === 'string' && this.isSharedSizeAvailable(stored.sharedSize)) {
            this.sharedSize = stored.sharedSize;
          }
          if (stored.separateSizes && typeof stored.separateSizes === 'object' && !Array.isArray(stored.separateSizes)) {
            this.getSelectedProducts().forEach((product) => {
              const size = stored.separateSizes[product.key];
              if (typeof size === 'string' && this.getVariant(product, size)) {
                this.separateSizes.set(product.key, size);
              }
            });
          }
        }

        this.currentStep = stored.uiStep === 'sizes' && this.selectedKeys.length === 2 ? 'sizes' : 'motifs';
        this.addSucceeded = false;
        this.submissionError = false;
        return 'restored';
      }

      resetPersistedUserState() {
        this.selectedKeys = [];
        this.mode = 'shared';
        this.sharedSize = null;
        this.separateSizes.clear();
        this.currentStep = 'motifs';
        this.addSucceeded = false;
        this.submissionError = false;
        this.suppressStepDetectionUntil = 0;
        this.clearMessage();
      }

      persistState() {
        if (!this.storageKey || !this.isConfigured) return;
        const separateSizes = {};
        this.separateSizes.forEach((size, key) => {
          if (this.selectedKeys.includes(key) && this.getVariant(this.productsByKey.get(key), size)) {
            separateSizes[key] = size;
          }
        });
        const state = {
          version: this.storageVersion,
          scope: this.storageScope,
          productSignature: this.productSignature,
          savedAt: Date.now(),
          selectedKeys: [...this.selectedKeys],
          mode: this.mode,
          sharedSize: this.sharedSize,
          separateSizes,
          uiStep: this.currentStep,
        };
        try {
          window.localStorage.setItem(this.storageKey, JSON.stringify(state));
        } catch (_error) {
          // The picker remains fully usable when storage is blocked or full.
        }
      }

      clearPersistentState() {
        if (!this.storageKey) return;
        try {
          window.localStorage.removeItem(this.storageKey);
        } catch (_error) {
          // Ignore browsers that deny storage access.
        }
      }

      hydrateCards() {
        this.cards.forEach((card) => {
          const product = this.productsByKey.get(card.dataset.productKey);
          if (!product) {
            card.disabled = true;
            card.classList.add('is-invalid');
            return;
          }

          const color = card.querySelector('[data-card-color]');
          const price = card.querySelector('[data-card-price]');
          const image = card.querySelector('img');
          if (color) color.textContent = product.fixedColor || 'Ohne Farboption';
          if (price) price.textContent = this.formatMoney(product.fixedVariants[0]?.price || 0, product.currency);
          if (image && product.image) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = product.image;
          }

          if (!product.valid) {
            card.disabled = true;
            card.classList.add('is-invalid');
            card.setAttribute('aria-label', `${product.displayTitle}, derzeit nicht konfiguriert oder verfügbar`);
            const action = card.querySelector('[data-card-action]');
            if (action) action.textContent = 'Nicht verfügbar';
          }
        });
      }

      bindEvents() {
        this.cards.forEach((card) => {
          card.addEventListener('click', () => {
            if (this.suppressCardClick || card.disabled) return;
            this.toggleProduct(card.dataset.productKey);
          });
        });

        this.slots.forEach((slot) => {
          slot.addEventListener('click', () => this.removeSelectionAt(Number(slot.dataset.selectionSlot)));
        });

        this.previousButton?.addEventListener('click', () => this.moveDeck(-1));
        this.nextButton?.addEventListener('click', () => this.moveDeck(1));
        this.continueButton?.addEventListener('click', () => this.goToSizes('primary_cta'));
        this.querySelector('[data-back-to-motifs]')?.addEventListener('click', () => this.goToMotifs('back_link'));
        this.appMotifStep?.addEventListener('click', () => this.goToMotifs('app_step'));
        this.appSizeStep?.addEventListener('click', () => this.goToSizes('app_step'));
        this.appCta?.addEventListener('click', () => this.handleAppCta());
        this.querySelectorAll('[data-size-mode]').forEach((button) => {
          button.addEventListener('click', () => this.setSizeMode(button.dataset.sizeMode));
        });
        this.addButton?.addEventListener('click', () => this.addBoth('inline_cta'));
        this.querySelectorAll('[data-dialog-close], [data-dialog-overlay]').forEach((button) => {
          button.addEventListener('click', () => this.closeDialog());
        });

        this.deck?.addEventListener('scroll', () => this.handleDeckScroll(), { passive: true });
        this.deck?.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          this.moveDeck(event.key === 'ArrowLeft' ? -1 : 1);
        });
        this.deck?.addEventListener('pointerdown', (event) => this.startDeckDrag(event));
        this.deck?.addEventListener('pointermove', (event) => this.moveDeckDrag(event));
        this.deck?.addEventListener('pointerup', (event) => this.endDeckDrag(event));
        this.deck?.addEventListener('pointercancel', (event) => this.endDeckDrag(event));

        this.boundResize = () => this.updateDeckPosition();
        this.boundDocumentKeydown = (event) => this.handleDocumentKeydown(event);
        this.boundPageHide = () => this.persistState();
        this.boundPageShow = (event) => this.handlePageShow(event);
        this.boundMotionChange = (event) => {
          this.reducedMotion = event.matches;
        };
        this.bindGlobalEvents();
      }

      bindGlobalEvents() {
        if (this.globalEventsBound || !this.boundResize) return;
        window.addEventListener('resize', this.boundResize, { passive: true });
        window.addEventListener('pagehide', this.boundPageHide);
        window.addEventListener('pageshow', this.boundPageShow);
        document.addEventListener('keydown', this.boundDocumentKeydown);
        this.reducedMotionQuery.addEventListener?.('change', this.boundMotionChange);
        this.globalEventsBound = true;
      }

      unbindGlobalEvents() {
        if (!this.globalEventsBound) return;
        document.removeEventListener('keydown', this.boundDocumentKeydown);
        window.removeEventListener('resize', this.boundResize);
        window.removeEventListener('pagehide', this.boundPageHide);
        window.removeEventListener('pageshow', this.boundPageShow);
        this.reducedMotionQuery.removeEventListener?.('change', this.boundMotionChange);
        this.globalEventsBound = false;
      }

      startDeckDrag(event) {
        if (event.pointerType === 'touch' || event.button !== 0) return;
        this.dragState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startScrollLeft: this.deck.scrollLeft,
          moved: false,
          captured: false,
        };
      }

      moveDeckDrag(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
        const distance = event.clientX - this.dragState.startX;
        if (Math.abs(distance) > 5 && !this.dragState.moved) {
          this.dragState.moved = true;
          this.deck.setPointerCapture?.(event.pointerId);
          this.dragState.captured = this.deck.hasPointerCapture?.(event.pointerId) || false;
          this.deck.classList.add('is-dragging');
        }
        if (!this.dragState.moved) return;
        event.preventDefault();
        this.deck.scrollLeft = this.dragState.startScrollLeft - distance;
      }

      endDeckDrag(event) {
        if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;
        const moved = this.dragState.moved;
        const captured = this.dragState.captured;
        if (captured && this.deck.hasPointerCapture?.(event.pointerId)) this.deck.releasePointerCapture(event.pointerId);
        this.deck.classList.remove('is-dragging');
        this.dragState = null;

        if (moved) {
          this.suppressCardClick = true;
          window.setTimeout(() => {
            this.suppressCardClick = false;
          }, 180);
          this.scrollToCard(this.getNearestCardIndex());
        }
      }

      handleDeckScroll() {
        if (this.deckFrame) return;
        this.deckFrame = window.requestAnimationFrame(() => {
          this.deckFrame = null;
          this.updateDeckPosition();
        });
      }

      getNearestCardIndex() {
        if (!this.cards.length) return 0;
        const deckCenter = this.deck.scrollLeft + (this.deck.clientWidth / 2);
        let nearestIndex = 0;
        let nearestDistance = Number.POSITIVE_INFINITY;

        this.cards.forEach((card, index) => {
          const cardCenter = card.offsetLeft + (card.offsetWidth / 2);
          const distance = Math.abs(cardCenter - deckCenter);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });
        return nearestIndex;
      }

      updateDeckPosition() {
        if (!this.cards.length) {
          if (this.previousButton) this.previousButton.disabled = true;
          if (this.nextButton) this.nextButton.disabled = true;
          return;
        }
        this.activeCardIndex = this.getNearestCardIndex();
        if (this.previousButton) this.previousButton.disabled = this.activeCardIndex <= 0;
        if (this.nextButton) this.nextButton.disabled = this.activeCardIndex >= this.cards.length - 1;
        if (this.dots) {
          [...this.dots.children].forEach((dot, index) => dot.classList.toggle('is-active', index === this.activeCardIndex));
        }
      }

      moveDeck(direction) {
        const nextIndex = Math.max(0, Math.min(this.cards.length - 1, this.getNearestCardIndex() + direction));
        this.scrollToCard(nextIndex);
      }

      scrollToCard(index) {
        const card = this.cards[index];
        if (!card) return;
        const left = card.offsetLeft - ((this.deck.clientWidth - card.offsetWidth) / 2);
        this.deck.scrollTo({
          left,
          behavior: this.reducedMotion ? 'auto' : 'smooth',
        });
        if (this.reducedMotion) this.updateDeckPosition();
      }

      createDots() {
        if (!this.dots) return;
        const dots = this.cards.map(() => document.createElement('span'));
        this.dots.replaceChildren(...dots);
      }

      toggleProduct(key) {
        if (this.isSubmitting) return;
        const product = this.productsByKey.get(key);
        if (!product?.valid || !this.isConfigured) return;
        const selectedIndex = this.selectedKeys.indexOf(key);
        const wasReady = this.getCheckoutState().ready;
        let action;
        let slotIndex;

        if (selectedIndex >= 0) {
          action = 'removed';
          slotIndex = selectedIndex + 1;
          this.selectedKeys.splice(selectedIndex, 1);
          this.setStatus(`${product.displayTitle} wurde aus deinem Duo entfernt.`);
        } else if (this.selectedKeys.length < 2) {
          this.selectedKeys.push(key);
          action = 'selected';
          slotIndex = this.selectedKeys.length;
          this.setStatus(`${product.displayTitle} ist Auswahl ${this.selectedKeys.length} von 2.`);
        } else {
          this.showMessage('Dein Duo ist voll – entferne zuerst ein Motiv.');
          this.setStatus('Dein Duo ist voll – entferne zuerst ein Motiv.');
          return;
        }

        this.resetSizes();
        this.updateSelectionUI();
        this.persistState();
        const state = this.getCheckoutState();
        this.track('two_shirt_v4_motif_changed', {
          action,
          interaction_source: 'card',
          slot_index: slotIndex,
          product_id: String(product.id),
          item_name: product.displayTitle,
          was_ready: Number(wasReady),
          is_ready: Number(state.ready),
        }, state);
        if (action === 'selected' && this.selectedKeys.length === 2) {
          this.track('two_shirt_v4_motifs_complete', {
            completion_source: 'card',
          }, state);
        }
      }

      removeSelectionAt(index) {
        if (this.isSubmitting) return;
        const key = this.selectedKeys[index];
        if (!key) return;
        const product = this.productsByKey.get(key);
        const wasReady = this.getCheckoutState().ready;
        this.selectedKeys.splice(index, 1);
        this.resetSizes();
        this.updateSelectionUI();
        this.persistState();
        this.setStatus(`${product?.displayTitle || 'Das Motiv'} wurde aus deinem Duo entfernt.`);
        const state = this.getCheckoutState();
        this.track('two_shirt_v4_motif_changed', {
          action: 'removed',
          interaction_source: 'selection_slot',
          slot_index: index + 1,
          product_id: String(product?.id || ''),
          item_name: product?.displayTitle || '',
          was_ready: Number(wasReady),
          is_ready: Number(state.ready),
        }, state);
      }

      resetSizes() {
        this.sharedSize = null;
        this.separateSizes.clear();
        this.addSucceeded = false;
        this.submissionError = false;
        this.clearMessage();
      }

      updateSelectionUI() {
        const count = this.selectedKeys.length;
        this.cards.forEach((card) => {
          const selected = this.selectedKeys.includes(card.dataset.productKey);
          card.classList.toggle('is-selected', selected);
          card.setAttribute('aria-pressed', selected ? 'true' : 'false');
          const action = card.querySelector('[data-card-action]');
          if (action && !card.disabled) {
            action.textContent = selected ? 'Auswahl entfernen' : (count === 2 ? 'Duo vollständig' : 'Motiv wählen');
          }
        });

        this.slots.forEach((slot, index) => this.renderSlot(slot, this.productsByKey.get(this.selectedKeys[index]), index));
        if (this.selectionCount) this.selectionCount.textContent = `${count} von 2`;
        if (this.stepKicker) this.stepKicker.textContent = `Schritt 1 von 2 · ${count}/2 gewählt`;

        if (this.continueButton) {
          this.continueButton.disabled = count !== 2 || !this.isConfigured;
          this.continueButton.textContent = count === 2
            ? 'Weiter zu Größen'
            : `Noch ${2 - count} ${count === 1 ? 'Motiv' : 'Motive'} wählen`;
        }

        const readyForSizes = count === 2 && this.isConfigured;
        this.sizeStep.hidden = !readyForSizes;
        if (readyForSizes) {
          this.renderSizeStep();
        } else {
          this.currentStep = 'motifs';
        }
        this.syncStagePanels();
        this.updateDuoPrice();
        this.updateProgress();
        this.updateAppBar();
      }

      syncStagePanels() {
        const showMotifs = this.currentStep === 'motifs';
        const showSizes = this.currentStep === 'sizes';
        const setPanelState = (panel, visible) => {
          if (!panel) return;
          panel.hidden = !visible;
          panel.inert = !visible;
          if (visible) panel.removeAttribute('inert');
          else panel.setAttribute('inert', '');
        };
        setPanelState(this.motifStep, showMotifs);
        setPanelState(this.sizeStep, showSizes);
        this.dataset.stage = this.currentStep;
      }

      renderSlot(slot, product, index) {
        const media = slot.querySelector('[data-slot-media]');
        const title = slot.querySelector('[data-slot-title]');
        const color = slot.querySelector('[data-slot-color]');
        media.replaceChildren();

        if (!product) {
          slot.disabled = true;
          slot.setAttribute('aria-label', `Auswahlplatz ${index + 1}, noch leer`);
          title.textContent = 'Motiv wählen';
          color.textContent = 'noch frei';
          return;
        }

        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = '';
          image.width = 104;
          image.height = 104;
          media.append(image);
        }
        slot.disabled = false;
        slot.setAttribute('aria-label', `${product.displayTitle} aus Auswahlplatz ${index + 1} entfernen`);
        title.textContent = product.displayTitle;
        color.textContent = product.fixedColor || 'Ohne Farboption';
      }

      renderSizeStep() {
        this.renderChosenProducts();
        this.renderSizeOptions();
        this.updateSizeModeUI();
        this.updateAddState();
      }

      renderChosenProducts() {
        const rows = this.getSelectedProducts().map((product, index) => {
          const row = document.createElement('article');
          row.className = 'free-two-shirt-v4__chosen-product';

          if (product.image) {
            const image = document.createElement('img');
            image.src = product.image;
            image.alt = '';
            image.width = 112;
            image.height = 112;
            row.append(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.className = 'free-two-shirt-v4__slot-media';
            placeholder.setAttribute('aria-hidden', 'true');
            row.append(placeholder);
          }

          const copy = document.createElement('div');
          const title = document.createElement('strong');
          title.textContent = product.displayTitle;
          const meta = document.createElement('span');
          meta.textContent = `${product.fixedColor || 'Ohne Farboption'} · ${this.formatMoney(product.fixedVariants[0]?.price || 0, product.currency)}`;
          copy.append(title, meta);

          const selection = document.createElement('b');
          selection.dataset.chosenSize = product.key;
          selection.textContent = 'Größe wählen';
          row.append(copy, selection);
          row.setAttribute('aria-label', `Auswahl ${index + 1}: ${product.displayTitle}`);
          return row;
        });
        this.chosenProducts.replaceChildren(...rows);
      }

      renderSizeOptions() {
        const sharedContainer = this.querySelector('[data-size-options="shared"]');
        if (sharedContainer) {
          const sizes = this.getSharedSizes();
          sharedContainer.replaceChildren(...sizes.map((size) => this.createSizeButton('shared', size)));
        }

        const groups = this.getSelectedProducts().map((product, index) => {
          const fieldset = document.createElement('fieldset');
          fieldset.className = 'free-two-shirt-v4__fieldset';
          const legend = document.createElement('legend');
          legend.textContent = `0${index + 1} · ${product.displayTitle}`;
          const options = document.createElement('div');
          options.className = 'free-two-shirt-v4__sizes';
          options.dataset.sizeOptions = product.key;
          options.setAttribute('role', 'radiogroup');
          options.setAttribute('aria-label', `Größe für ${product.displayTitle}`);
          options.replaceChildren(...product.sizes.map((size) => this.createSizeButton(product.key, size)));
          fieldset.append(legend, options);
          return fieldset;
        });
        this.separateGroups.replaceChildren(...groups);
        this.updateSizeRovingTabindexes();

        const hasSharedSize = this.getSharedSizes().some((size) => this.isSharedSizeAvailable(size));
        if (this.mode === 'shared' && !hasSharedSize) {
          this.showMessage('Für dieses Duo ist gerade keine gemeinsame Größe verfügbar. Wähle „Zwei Größen“.');
        }
      }

      createSizeButton(key, size) {
        const product = key === 'shared' ? null : this.productsByKey.get(key);
        const selected = key === 'shared' ? this.sharedSize === size : this.separateSizes.get(key) === size;
        const available = key === 'shared' ? this.isSharedSizeAvailable(size) : Boolean(this.getVariant(product, size));
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'free-two-shirt-v4__size';
        button.textContent = size;
        button.dataset.size = size;
        button.dataset.productKey = key;
        button.setAttribute('role', 'radio');
        button.setAttribute('aria-checked', selected ? 'true' : 'false');
        button.disabled = !available;
        button.tabIndex = -1;
        button.setAttribute('aria-label', available ? `Größe ${size}` : `Größe ${size}, nicht verfügbar`);
        button.addEventListener('click', () => this.selectSize(key, size));
        button.addEventListener('keydown', (event) => this.handleSizeKeydown(event));
        return button;
      }

      updateSizeRovingTabindexes() {
        this.querySelectorAll('[data-size-options][role="radiogroup"]').forEach((group) => {
          const enabled = [...group.querySelectorAll('.free-two-shirt-v4__size:not(:disabled)')];
          const selected = enabled.find((button) => button.getAttribute('aria-checked') === 'true');
          enabled.forEach((button) => {
            button.tabIndex = button === (selected || enabled[0]) ? 0 : -1;
          });
          group.querySelectorAll('.free-two-shirt-v4__size:disabled').forEach((button) => {
            button.tabIndex = -1;
          });
        });
      }

      handleSizeKeydown(event) {
        const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (!keys.includes(event.key)) return;
        const group = event.currentTarget.closest('[role="radiogroup"]');
        const enabled = group ? [...group.querySelectorAll('.free-two-shirt-v4__size:not(:disabled)')] : [];
        if (!enabled.length) return;
        event.preventDefault();
        const currentIndex = Math.max(0, enabled.indexOf(event.currentTarget));
        let targetIndex;
        if (event.key === 'Home') targetIndex = 0;
        else if (event.key === 'End') targetIndex = enabled.length - 1;
        else {
          const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
          targetIndex = (currentIndex + direction + enabled.length) % enabled.length;
        }
        const target = enabled[targetIndex];
        this.selectSize(target.dataset.productKey, target.dataset.size);
      }

      getSharedSizes() {
        const sizes = [];
        this.getSelectedProducts().forEach((product) => {
          product.sizes.forEach((size) => {
            if (!sizes.includes(size)) sizes.push(size);
          });
        });
        return sizes;
      }

      isSharedSizeAvailable(size) {
        const products = this.getSelectedProducts();
        return products.length === 2 && products.every((product) => Boolean(this.getVariant(product, size)));
      }

      getVariant(product, size, requireAvailable = true) {
        if (!product || !size) return null;
        return product.fixedVariants.find((variant) => {
          const sizeMatches = variant.options[product.sizeIndex] === size;
          const available = variant.available && !this.liveUnavailable.has(String(variant.id));
          return sizeMatches && (!requireAvailable || available);
        }) || null;
      }

      selectSize(key, size) {
        if (this.isSubmitting) return;
        const previousSize = key === 'shared' ? this.sharedSize : this.separateSizes.get(key);
        if (previousSize === size) return;
        const wasReady = this.getCheckoutState().ready;
        this.clearMessage();
        this.addSucceeded = false;
        this.submissionError = false;
        if (key === 'shared') this.sharedSize = size;
        else this.separateSizes.set(key, size);
        this.renderSizeOptions();
        this.updateAddState();
        this.persistState();
        const visiblePanel = this.mode === 'shared' ? this.sharedPanel : this.separatePanel;
        [...visiblePanel.querySelectorAll('.free-two-shirt-v4__size:not(:disabled)')]
          .find((button) => button.dataset.productKey === key && button.dataset.size === size)
          ?.focus({ preventScroll: true });
        this.setStatus(key === 'shared' ? `Größe ${size} ist für beide Shirts gewählt.` : `Größe ${size} wurde gewählt.`);
        const state = this.getCheckoutState();
        const product = key === 'shared' ? null : this.productsByKey.get(key);
        const productIndex = product ? this.getSelectedProducts().findIndex((candidate) => candidate.key === key) : -1;
        this.track('two_shirt_v4_size_selected', {
          size,
          size_target: key === 'shared' ? 'shared' : `motif_${productIndex + 1}`,
          sizes_selected: this.selectedSizeCount(),
          sizes_required: this.mode === 'shared' ? 1 : 2,
          ...(product ? {
            product_id: String(product.id),
            item_name: product.displayTitle,
          } : {}),
          was_ready: Number(wasReady),
          is_ready: Number(state.ready),
        }, state);
        this.trackSelectionCompletion(wasReady, state);
      }

      setSizeMode(mode) {
        if (this.isSubmitting) return;
        if (!['shared', 'separate'].includes(mode) || this.mode === mode) return;
        const previousMode = this.mode;
        const wasReady = this.getCheckoutState().ready;
        this.clearMessage();
        this.addSucceeded = false;
        this.submissionError = false;

        if (mode === 'separate' && this.sharedSize) {
          this.getSelectedProducts().forEach((product) => {
            if (this.getVariant(product, this.sharedSize)) this.separateSizes.set(product.key, this.sharedSize);
          });
        }

        if (mode === 'shared') {
          const separateValues = this.getSelectedProducts().map((product) => this.separateSizes.get(product.key));
          this.sharedSize = separateValues.length === 2
            && separateValues[0]
            && separateValues[0] === separateValues[1]
            && this.isSharedSizeAvailable(separateValues[0])
            ? separateValues[0]
            : null;
        }

        this.mode = mode;
        this.updateSizeModeUI();
        this.renderSizeOptions();
        this.updateAddState();
        this.persistState();
        this.setStatus(mode === 'shared' ? 'Eine Größe für beide Shirts.' : 'Für jedes Shirt kann eine eigene Größe gewählt werden.');
        const state = this.getCheckoutState();
        this.track('two_shirt_v4_size_mode_changed', {
          from_mode: previousMode,
          to_mode: mode,
          sizes_selected: this.selectedSizeCount(),
          sizes_required: mode === 'shared' ? 1 : 2,
          was_ready: Number(wasReady),
          is_ready: Number(state.ready),
        }, state);
        this.trackSelectionCompletion(wasReady, state);
      }

      updateSizeModeUI() {
        this.querySelectorAll('[data-size-mode]').forEach((button) => {
          button.setAttribute('aria-pressed', button.dataset.sizeMode === this.mode ? 'true' : 'false');
        });
        this.sharedPanel.hidden = this.mode !== 'shared';
        this.separatePanel.hidden = this.mode !== 'separate';
      }

      getSelectedProducts() {
        return this.selectedKeys.map((key) => this.productsByKey.get(key)).filter(Boolean);
      }

      getSelections() {
        return this.getSelectedProducts().map((product) => {
          const size = this.mode === 'shared' ? this.sharedSize : this.separateSizes.get(product.key);
          return { product, size, variant: this.getVariant(product, size) };
        });
      }

      updateDuoPrice(state = this.getCheckoutState()) {
        if (!this.duoPrice) return;
        this.duoPrice.textContent = state.selections.length === 2 && state.total > 0
          ? this.formatMoney(state.total, state.currency)
          : this.initialDuoPrice;
      }

      updateAddState() {
        const state = this.getCheckoutState();
        const { selections, ready, missing, total } = state;

        selections.forEach(({ product, size }) => {
          const label = [...this.querySelectorAll('[data-chosen-size]')]
            .find((element) => element.dataset.chosenSize === product.key);
          if (label) label.textContent = size ? `Größe ${size}` : 'Größe wählen';
        });

        if (!this.isSubmitting) this.addButton.disabled = !ready;
        if (ready) {
          this.addLabel.textContent = `2 Shirts in den Warenkorb · ${this.formatMoney(total, state.currency)}`;
        } else if (this.mode === 'shared') {
          this.addLabel.textContent = 'Eine Größe für beide wählen';
        } else {
          this.addLabel.textContent = missing === 1 ? 'Noch 1 Größe wählen' : 'Noch 2 Größen wählen';
        }
        this.updateDuoPrice(state);
        this.updateProgress(ready);
        this.updateAppBar(state);
      }

      getCheckoutState() {
        const selections = this.getSelections();
        const ready = selections.length === 2 && selections.every((selection) => Boolean(selection.variant));
        const missing = selections.filter((selection) => !selection.variant).length;
        const total = selections.reduce((sum, selection) => {
          return sum + Number(selection.variant?.price || selection.product.fixedVariants[0]?.price || 0);
        }, 0);
        return {
          selections,
          ready,
          missing,
          total,
          currency: selections[0]?.product.currency || this.products[0]?.currency || 'EUR',
        };
      }

      updateProgress(sizesReady = this.getCheckoutState().ready) {
        const motifStep = this.querySelector('[data-progress-step="motifs"]');
        const sizeProgress = this.querySelector('[data-progress-step="sizes"]');
        const motifsReady = this.selectedKeys.length === 2;

        [motifStep, sizeProgress].forEach((step) => {
          step?.classList.remove('is-active', 'is-complete');
          step?.removeAttribute('aria-current');
        });
        if (!motifsReady) {
          motifStep?.classList.add('is-active');
          motifStep?.setAttribute('aria-current', 'step');
          return;
        }
        motifStep?.classList.add('is-complete');
        sizeProgress?.classList.add(sizesReady ? 'is-complete' : 'is-active');
        if (!sizesReady) sizeProgress?.setAttribute('aria-current', 'step');
      }

      updateAppBar(state = this.getCheckoutState()) {
        if (!this.appBar) return;
        const count = this.selectedKeys.length;
        const sizesAvailable = count === 2 && this.isConfigured;
        let stage = this.currentStep;
        if (this.isSubmitting) stage = 'loading';
        else if (this.addSucceeded) stage = 'success';
        else if (this.submissionError && state.ready) stage = 'error';
        else if (state.ready) stage = 'ready';
        this.appBar.dataset.stage = stage;
        if (this.appMotifCount) this.appMotifCount.textContent = `${count}/2`;
        if (this.appSizeState) {
          this.appSizeState.textContent = !sizesAvailable
            ? 'gesperrt'
            : (state.ready ? 'gewählt' : `${this.mode === 'shared' ? 1 : Math.max(1, state.missing)} offen`);
        }

        this.appMotifStep.disabled = this.isSubmitting;
        this.appSizeStep.disabled = this.isSubmitting || !sizesAvailable;
        const activeAppStep = this.currentStep;
        [this.appMotifStep, this.appSizeStep].forEach((button) => {
          const active = button?.dataset.appStep === activeAppStep;
          button?.classList.toggle('is-active', active);
          if (active) button?.setAttribute('aria-current', 'step');
          else button?.removeAttribute('aria-current');
        });
        this.appMotifStep?.classList.toggle('is-complete', sizesAvailable);
        this.appSizeStep?.classList.toggle('is-complete', state.ready);

        let label;
        let disabled = true;
        let status = 'waiting';
        if (this.isSubmitting) {
          label = 'Wird hinzugefügt …';
          status = 'loading';
        } else if (this.addSucceeded) {
          label = '✓ 2 Shirts hinzugefügt · ansehen';
          disabled = false;
          status = 'success';
        } else if (count < 2) {
          label = `Noch ${2 - count} ${count === 1 ? 'Motiv' : 'Motive'} wählen`;
        } else if (state.ready) {
          label = this.submissionError
            ? `Erneut versuchen · ${this.formatMoney(state.total, state.currency)}`
            : `2 Shirts in den Warenkorb · ${this.formatMoney(state.total, state.currency)}`;
          disabled = false;
          status = this.submissionError ? 'error' : 'ready';
        } else if (this.currentStep === 'motifs') {
          label = 'Weiter zu Größen';
          disabled = false;
          status = 'continue';
        } else if (this.mode === 'shared') {
          label = 'Eine Größe für beide wählen';
        } else {
          label = state.missing === 1 ? 'Noch 1 Größe wählen' : 'Noch 2 Größen wählen';
        }

        this.appCta.disabled = disabled;
        this.appCta.dataset.state = status;
        this.appCta.classList.toggle('is-loading', status === 'loading');
        this.appCtaLabel.textContent = label;
      }

      handleAppCta() {
        if (this.isSubmitting || this.appCta?.disabled) return;
        if (this.addSucceeded) {
          this.reopenSuccessDialog();
          return;
        }
        const state = this.getCheckoutState();
        if (state.ready) {
          this.addBoth('app_cta');
          return;
        }
        if (this.selectedKeys.length === 2 && this.currentStep === 'motifs') {
          this.goToSizes('app_cta');
        }
      }

      goToSizes(navigationSource = 'unknown') {
        if (this.isSubmitting || this.selectedKeys.length !== 2 || !this.isConfigured) return;
        const previousStep = this.currentStep;
        this.currentStep = 'sizes';
        this.syncStagePanels();
        this.updateProgress();
        this.updateAppBar();
        this.persistState();
        if (previousStep !== this.currentStep) {
          this.track('two_shirt_v4_step_view', {
            direction: 'forward',
            navigation_source: navigationSource,
            previous_step: previousStep,
          });
        }
        this.stageAnchor?.scrollIntoView({
          block: 'start',
          behavior: 'auto',
        });
        window.setTimeout(() => {
          const visiblePanel = this.mode === 'shared' ? this.sharedPanel : this.separatePanel;
          const enabled = [...visiblePanel.querySelectorAll('.free-two-shirt-v4__size:not(:disabled)')];
          const enabledSize = enabled.find((button) => button.getAttribute('aria-checked') === 'true') || enabled[0];
          const fallback = this.mode === 'shared'
            ? this.querySelector('[data-size-mode="separate"]')
            : this.querySelector(`[data-size-mode="${this.mode}"]`) || this.querySelector('[data-back-to-motifs]');
          (enabledSize || fallback)?.focus({ preventScroll: true });
        }, this.reducedMotion ? 0 : 240);
      }

      goToMotifs(navigationSource = 'unknown') {
        if (this.isSubmitting) return;
        const previousStep = this.currentStep;
        this.currentStep = 'motifs';
        this.syncStagePanels();
        this.updateProgress();
        this.updateAppBar();
        this.persistState();
        if (previousStep !== this.currentStep) {
          this.track('two_shirt_v4_step_view', {
            direction: 'back',
            navigation_source: navigationSource,
            previous_step: previousStep,
          });
        }
        this.stageAnchor?.scrollIntoView({
          block: 'start',
          behavior: 'auto',
        });
        window.setTimeout(() => {
          const activeCard = this.cards[this.activeCardIndex];
          const activeCardIsVisible = activeCard
            && activeCard.offsetLeft < this.deck.scrollLeft + this.deck.clientWidth
            && activeCard.offsetLeft + activeCard.offsetWidth > this.deck.scrollLeft;
          const target = (activeCardIsVisible && !activeCard.disabled && activeCard)
            || this.cards.find((card) => card.getAttribute('aria-pressed') === 'true' && !card.disabled)
            || this.cards.find((card) => !card.disabled);
          target?.focus({ preventScroll: true });
        }, this.reducedMotion ? 0 : 240);
      }

      handlePageShow(event) {
        if (!event.persisted) return;
        this.clearSuccessOpenTimer();
        this.isSubmitting = false;
        this.setAttribute('aria-busy', 'false');
        this.addSucceeded = false;
        this.submissionError = false;
        this.clearMessage();
        this.closeDialog();
        const restoreStatus = this.restorePersistentState();
        if (restoreStatus === 'missing' || restoreStatus === 'invalid') {
          this.resetPersistedUserState();
        }
        this.addButton?.classList.remove('is-loading');
        this.appCta?.classList.remove('is-loading');
        this.updateSelectionUI();
      }

      async validateLiveAvailability(selections) {
        const liveProducts = await Promise.all(selections.map(async (selection) => {
          const response = await fetch(this.asJsonEndpoint(selection.product.url), {
            headers: { Accept: 'application/json' },
            cache: 'no-store',
          });
          if (!response.ok) throw new Error('Die Live-Verfügbarkeit konnte gerade nicht geprüft werden. Bitte versuche es erneut.');
          return { selection, product: await response.json() };
        }));

        let allAvailable = true;
        liveProducts.forEach(({ selection, product }) => {
          const liveVariant = product.variants?.find((variant) => String(variant.id) === String(selection.variant.id));
          if (liveVariant?.available) return;
          allAvailable = false;
          this.liveUnavailable.add(String(selection.variant.id));
          selection.variant.available = false;
        });

        if (!allAvailable) {
          if (this.mode === 'shared' && !this.isSharedSizeAvailable(this.sharedSize)) this.sharedSize = null;
          if (this.mode === 'separate') {
            selections.forEach(({ product, size }) => {
              if (!this.getVariant(product, size)) this.separateSizes.delete(product.key);
            });
          }
          this.renderSizeOptions();
          this.updateAddState();
          this.persistState();
          this.showMessage('Eine gewählte Größe ist gerade nicht mehr verfügbar. Bitte wähle eine andere.');
        }
        return allAvailable;
      }

      attributionCartLabels() {
        return {
          utm_source: 'Zwei-Shirts UTM Source',
          utm_medium: 'Zwei-Shirts UTM Medium',
          utm_campaign: 'Zwei-Shirts UTM Campaign',
          utm_content: 'Zwei-Shirts UTM Content',
          utm_term: 'Zwei-Shirts UTM Term',
        };
      }

      emptyAttributionCartAttributes() {
        const attributes = { 'Zwei-Shirts Journey': null };
        Object.values(this.attributionCartLabels()).forEach((label) => {
          attributes[label] = null;
        });
        return attributes;
      }

      async updateAttributionCartAttributes(attributes) {
        if (!this.dataset.cartUpdateUrl) return { status: 'skipped_unavailable' };
        Object.values(this.attributionCartLabels()).forEach((label) => {
          if (!(label in attributes)) attributes[label] = null;
        });
        const controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
        let timeoutId;
        try {
          const request = Promise.resolve(fetch(this.asJsonEndpoint(this.dataset.cartUpdateUrl), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes }),
            ...(controller ? { signal: controller.signal } : {}),
          })).then((response) => ({ response }), (error) => ({ error }));
          const timeout = new Promise((resolve) => {
            timeoutId = window.setTimeout(() => {
              controller?.abort();
              resolve({ timedOut: true });
            }, 1500);
          });
          const result = await Promise.race([request, timeout]);

          if (result.timedOut) return { status: 'timeout' };
          if (result.error) {
            return { status: result.error.name === 'AbortError' ? 'timeout' : 'network_error' };
          }
          if (!result.response.ok) {
            return {
              status: 'http_error',
              ...(Number.isFinite(Number(result.response.status)) ? { httpStatus: Number(result.response.status) } : {}),
            };
          }
          return { status: 'succeeded' };
        } catch (_error) {
          return { status: 'network_error' };
        } finally {
          if (timeoutId) window.clearTimeout(timeoutId);
        }
      }

      async persistAttribution() {
        if (!this.analytics.isAnalyticsAllowed()) return { status: 'skipped_no_consent' };
        const labels = this.attributionCartLabels();
        const attributes = this.emptyAttributionCartAttributes();
        attributes['Zwei-Shirts Journey'] = 'V4';
        Object.entries(this.attribution || {}).forEach(([key, value]) => {
          if (labels[key] && value) attributes[labels[key]] = value;
        });
        return this.updateAttributionCartAttributes(attributes);
      }

      async clearPersistedAttribution() {
        return this.updateAttributionCartAttributes(this.emptyAttributionCartAttributes());
      }

      async addBoth(interactionSource = 'unknown') {
        if (this.isSubmitting) return;
        const selections = this.getSelections();
        if (selections.length !== 2 || !selections.every((selection) => selection.variant)) return;
        const checkoutState = this.getCheckoutState();
        const analyticsPayload = this.checkoutAnalyticsPayload(selections, checkoutState);
        const isRetry = this.submissionError;
        const attemptStartedAt = window.performance.now();

        const activeElement = document.activeElement;
        this.lastFocusedElement = activeElement instanceof HTMLElement && activeElement !== document.body
          ? activeElement
          : this.appCta;
        this.clearMessage();
        this.submissionError = false;
        this.setLoading(true);
        this.track('two_shirt_v4_add_clicked', {
          ...analyticsPayload,
          interaction_source: interactionSource,
          is_retry: Number(isRetry),
        }, checkoutState);
        let cartBefore = null;
        let mutationAttempted = false;
        let failureStage = 'availability_check';

        try {
          if (!(await this.validateLiveAvailability(selections))) {
            this.track('two_shirt_v4_add_failed', {
              ...analyticsPayload,
              error_stage: 'availability_check',
              error_code: 'availability_changed',
              rollback_status: 'not_needed',
              duration_ms: Math.round(window.performance.now() - attemptStartedAt),
              interaction_source: interactionSource,
              is_retry: Number(isRetry),
            });
            return;
          }
          failureStage = 'cart_read_before';
          cartBefore = await this.getCart();
          mutationAttempted = true;

          failureStage = 'cart_add';
          const response = await fetch(this.asJsonEndpoint(this.dataset.cartAddUrl), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({
              items: selections.map(({ variant }) => ({
                id: Number(variant.id),
                quantity: 1,
                properties: { _lexibo_experiment: 'EXP-071' },
              })),
              sections: ['cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const result = await response.json().catch(() => ({}));
          if (!response.ok) {
            const cartAddError = new Error(result.description || 'Die beiden Shirts konnten nicht gemeinsam hinzugefügt werden.');
            cartAddError.analyticsCode = 'cart_add_rejected';
            cartAddError.httpStatus = response.status;
            throw cartAddError;
          }

          failureStage = 'cart_read_after';
          const cartAfter = await this.getCart();
          failureStage = 'cart_delta_verify';
          if (!this.hasExpectedCartDelta(cartBefore, cartAfter, selections)) {
            const verificationError = new Error('Die beiden Shirts konnten nicht vollständig hinzugefügt werden.');
            verificationError.analyticsCode = 'cart_delta_mismatch';
            throw verificationError;
          }

          const attributionResult = await this.persistAttribution();
          this.updateCartIcon(result.sections?.['cart-icon-bubble']);
          this.addSucceeded = true;
          this.submissionError = false;
          this.updateProgress(true);
          this.updateAppBar();
          this.persistState();
          this.lastSuccess = { selections, cart: cartAfter };
          this.scheduleSuccessOpen();
          mutationAttempted = false;
          const successState = this.getCheckoutState();
          this.track('two_shirt_v4_add_succeeded', {
            shirt_count: selections.length,
            duration_ms: Math.round(window.performance.now() - attemptStartedAt),
            interaction_source: interactionSource,
            is_retry: Number(isRetry),
            attribution_status: attributionResult.status,
            ...(attributionResult.httpStatus ? { attribution_http_status: attributionResult.httpStatus } : {}),
          }, successState);
        } catch (error) {
          let rollbackFailed = false;
          let rollbackStatus = 'not_needed';
          if (mutationAttempted && cartBefore) {
            rollbackStatus = 'succeeded';
            try {
              await this.restoreAffectedCartLines(cartBefore, selections);
            } catch (rollbackError) {
              rollbackFailed = true;
              rollbackStatus = 'failed';
              console.error('Der Warenkorb konnte nicht vollständig auf den Ausgangsstand zurückgesetzt werden.', rollbackError);
            }
          }

          const baseMessage = error.message || 'Das hat gerade nicht geklappt. Bitte versuche es erneut.';
          this.submissionError = true;
          this.showMessage(rollbackFailed ? `${baseMessage} Bitte prüfe vorsichtshalber deinen Warenkorb.` : baseMessage);
          const fallbackErrorCodes = {
            availability_check: 'network_error',
            cart_read_before: 'cart_read_failed',
            cart_add: 'network_error',
            cart_read_after: 'cart_read_failed',
            cart_delta_verify: 'cart_delta_mismatch',
          };
          this.track('two_shirt_v4_add_failed', {
            ...analyticsPayload,
            error_stage: failureStage,
            error_code: error.analyticsCode || fallbackErrorCodes[failureStage] || 'unknown',
            rollback_status: rollbackStatus,
            duration_ms: Math.round(window.performance.now() - attemptStartedAt),
            interaction_source: interactionSource,
            is_retry: Number(isRetry),
            ...(Number.isFinite(Number(error.httpStatus)) ? { http_status: Number(error.httpStatus) } : {}),
          });
        } finally {
          this.setLoading(false);
        }
      }

      hasExpectedCartDelta(before, after, selections) {
        const expectedByVariant = new Map();
        selections.forEach(({ variant }) => {
          const id = String(variant.id);
          expectedByVariant.set(id, (expectedByVariant.get(id) || 0) + 1);
        });
        const beforeByVariant = this.quantitiesByVariant(before);
        const afterByVariant = this.quantitiesByVariant(after);
        return [...expectedByVariant.entries()].every(([id, delta]) => {
          return (afterByVariant.get(id) || 0) === (beforeByVariant.get(id) || 0) + delta;
        });
      }

      quantitiesByVariant(cart) {
        const quantities = new Map();
        (cart.items || []).forEach((item) => {
          const id = String(item.variant_id || item.id);
          quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
        });
        return quantities;
      }

      async restoreAffectedCartLines(snapshot, selections) {
        const affectedVariantIds = new Set(selections.map(({ variant }) => String(variant.id)));
        const current = await this.getCart();
        const snapshotBySignature = new Map();
        (snapshot.items || []).forEach((item) => {
          if (!affectedVariantIds.has(String(item.variant_id || item.id))) return;
          const signature = this.cartLineSignature(item);
          snapshotBySignature.set(signature, (snapshotBySignature.get(signature) || 0) + Number(item.quantity || 0));
        });
        const updates = {};
        const restoredSignatures = new Set();

        (current.items || []).forEach((item) => {
          if (!affectedVariantIds.has(String(item.variant_id || item.id))) return;
          const signature = this.cartLineSignature(item);
          updates[item.key] = restoredSignatures.has(signature) ? 0 : (snapshotBySignature.get(signature) || 0);
          restoredSignatures.add(signature);
        });

        if (Object.keys(updates).length) {
          const response = await fetch(this.asJsonEndpoint(this.dataset.cartUpdateUrl), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates }),
          });
          if (!response.ok) throw new Error('Warenkorb-Rücksetzung fehlgeschlagen.');
        }

        const restored = await this.getCart();
        const restoredBySignature = new Map();
        (restored.items || []).forEach((item) => {
          if (!affectedVariantIds.has(String(item.variant_id || item.id))) return;
          const signature = this.cartLineSignature(item);
          restoredBySignature.set(signature, (restoredBySignature.get(signature) || 0) + Number(item.quantity || 0));
        });
        const signatures = new Set([...snapshotBySignature.keys(), ...restoredBySignature.keys()]);
        const exact = [...signatures].every((signature) => {
          return (restoredBySignature.get(signature) || 0) === (snapshotBySignature.get(signature) || 0);
        });
        if (!exact) throw new Error('Warenkorb-Rücksetzung konnte nicht bestätigt werden.');
      }

      cartLineSignature(item) {
        const variantId = String(item.variant_id || item.id);
        const sellingPlanId = item.selling_plan_allocation?.selling_plan?.id
          || item.selling_plan_allocation?.selling_plan_id
          || item.selling_plan
          || '';
        const properties = Object.entries(item.properties || {}).sort(([left], [right]) => left.localeCompare(right));
        return `${variantId}|${sellingPlanId}|${JSON.stringify(properties)}`;
      }

      async getCart() {
        const response = await fetch(this.asJsonEndpoint(this.dataset.cartUrl), {
          headers: { Accept: 'application/json' },
          cache: 'no-store',
        });
        if (!response.ok) throw new Error('Der Warenkorb konnte nicht gelesen werden.');
        return response.json();
      }

      asJsonEndpoint(url) {
        const [path, query] = String(url || '').split('?');
        const jsonPath = path.endsWith('.js') ? path : `${path}.js`;
        return query ? `${jsonPath}?${query}` : jsonPath;
      }

      updateCartIcon(html) {
        if (!html) return;
        const target = document.getElementById('cart-icon-bubble');
        if (!target) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.getElementById('cart-icon-bubble');
        if (source) target.innerHTML = source.innerHTML;
      }

      openSuccessDialog(selections, cart) {
        const lines = selections.map(({ product, size, variant }) => {
          const row = document.createElement('article');
          row.className = 'free-two-shirt-v4__dialog-line';

          if (variant.image || product.image) {
            const image = document.createElement('img');
            image.src = variant.image || product.image;
            image.alt = '';
            image.width = 136;
            image.height = 136;
            row.append(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.className = 'free-two-shirt-v4__slot-media';
            placeholder.setAttribute('aria-hidden', 'true');
            row.append(placeholder);
          }

          const copy = document.createElement('div');
          const title = document.createElement('strong');
          title.textContent = product.displayTitle;
          const meta = document.createElement('span');
          meta.textContent = `${product.fixedColor || 'Ohne Farboption'} · Größe ${size}`;
          copy.append(title, meta);

          const price = document.createElement('b');
          price.textContent = this.formatMoney(variant.price, product.currency);
          row.append(copy, price);
          return row;
        });

        this.querySelector('[data-success-lines]').replaceChildren(...lines);
        const duoTotal = selections.reduce((sum, selection) => sum + Number(selection.variant.price || 0), 0);
        this.querySelector('[data-cart-total]').textContent = this.formatMoney(duoTotal, cart.currency || selections[0].product.currency);
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement && activeElement !== document.body) {
          this.lastFocusedElement = activeElement;
        } else if (!this.lastFocusedElement?.isConnected) {
          this.lastFocusedElement = this.appCta;
        }
        this.successDialog.classList.remove('is-closing');
        this.dialogOverlay.classList.remove('is-closing');
        this.dialogOverlay.hidden = false;
        this.successDialog.hidden = false;
        document.body.classList.add('free-two-shirt-v4-dialog-open');
        this.successDialog.querySelector('[data-dialog-close]')?.focus();
      }

      reopenSuccessDialog() {
        this.clearSuccessOpenTimer();
        if (this.successDialog && !this.successDialog.hidden) return;
        if (!this.lastSuccess) {
          this.setStatus('Die Bestätigung ist nicht mehr verfügbar. Dein Warenkorb bleibt unverändert.');
          return;
        }
        this.openSuccessDialog(this.lastSuccess.selections, this.lastSuccess.cart);
      }

      clearSuccessOpenTimer() {
        if (this.successOpenTimer === null) return;
        window.clearTimeout(this.successOpenTimer);
        this.successOpenTimer = null;
      }

      scheduleSuccessOpen() {
        this.clearSuccessOpenTimer();
        this.successOpenTimer = window.setTimeout(() => {
          this.successOpenTimer = null;
          this.reopenSuccessDialog();
        }, this.reducedMotion ? 0 : 200);
      }

      closeDialog() {
        if (this.successDialog?.hidden) return;
        document.body.classList.remove('free-two-shirt-v4-dialog-open');
        const finish = () => {
          this.dialogOverlay.hidden = true;
          this.successDialog.hidden = true;
          this.successDialog.classList.remove('is-closing');
          this.dialogOverlay.classList.remove('is-closing');
          this.updateAppBar();
          const returnTarget = this.lastFocusedElement?.isConnected && !this.lastFocusedElement.disabled
            ? this.lastFocusedElement
            : this.appCta;
          returnTarget?.focus?.({ preventScroll: true });
        };
        if (this.reducedMotion) {
          finish();
          return;
        }
        this.successDialog.classList.add('is-closing');
        this.dialogOverlay.classList.add('is-closing');
        window.setTimeout(finish, 260);
      }

      handleDocumentKeydown(event) {
        if (!this.successDialog || this.successDialog.hidden) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeDialog();
          return;
        }
        if (event.key !== 'Tab') return;

        const focusable = [...this.successDialog.querySelectorAll('a[href], button:not(:disabled)')];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      setLoading(loading) {
        this.isSubmitting = loading;
        this.setAttribute('aria-busy', loading ? 'true' : 'false');
        this.addButton.classList.toggle('is-loading', loading);
        if (loading) {
          this.addButton.disabled = true;
          this.addLabel.textContent = 'Verfügbarkeit wird geprüft …';
        } else {
          this.updateAddState();
        }
        this.updateAppBar();
      }

      showConfigurationMessage(text) {
        let element = this.querySelector('.free-two-shirt-v4__config-message');
        if (!element) {
          element = document.createElement('div');
          element.className = 'free-two-shirt-v4__config-message';
          element.setAttribute('role', 'status');
          this.querySelector('[data-motif-step]')?.before(element);
        }
        element.textContent = text;
      }

      showMessage(text) {
        if (this.message) this.message.textContent = text;
      }

      clearMessage() {
        if (this.message) this.message.textContent = '';
      }

      setStatus(text) {
        if (!this.liveStatus) return;
        this.liveStatus.textContent = '';
        window.requestAnimationFrame(() => {
          this.liveStatus.textContent = text;
        });
      }

      formatMoney(cents, currency = 'EUR') {
        return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
          style: 'currency',
          currency,
        }).format(Number(cents || 0) / 100);
      }
    }
  );
}
})();
